/**
 * Granel Vital — carril deslizable.
 *
 * Un carrusel que no secuestra el scroll: el track es un contenedor con
 * scroll nativo y scroll-snap, así que en móvil se arrastra con el dedo y en
 * escritorio se puede usar la rueda, el teclado o los botones. Nada de
 * temporizadores ni de reescribir el desplazamiento a mano.
 *
 * Los botones se apagan al llegar a cada extremo y el bloque de controles
 * desaparece entero cuando todo el contenido ya cabe a la vista — un carrusel
 * de tres tarjetas en un monitor ancho no necesita flechas.
 */
class GvRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]');
    if (!this.track) return;

    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');

    this.prev?.addEventListener('click', () => this.#page(-1));
    this.next?.addEventListener('click', () => this.#page(1));
    this.track.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#observer = new ResizeObserver(() => this.#sync());
    this.#observer.observe(this.track);
    // Las imágenes cambian el ancho del track al cargar.
    for (const img of this.track.querySelectorAll('img')) {
      if (!img.complete) img.addEventListener('load', () => this.#sync(), { once: true });
    }

    this.#sync();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    window.cancelAnimationFrame(this.#frame);
  }

  /** @type {ResizeObserver | null} */
  #observer = null;
  #frame = 0;

  /**
   * Avanza una «página»: casi todo el ancho visible, dejando un resto a la
   * vista para que se note que el carril continúa.
   * @param {number} direction - 1 hacia delante, -1 hacia atrás.
   */
  #page(direction) {
    const step = Math.max(this.track.clientWidth * 0.85, this.#itemWidth());
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: step * direction, behavior: reduced ? 'auto' : 'smooth' });
  }

  #itemWidth() {
    const first = this.track.firstElementChild;
    if (!first) return 240;
    const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  #onScroll = () => {
    window.cancelAnimationFrame(this.#frame);
    this.#frame = window.requestAnimationFrame(() => this.#sync());
  };

  #sync() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    // Un margen de 2px absorbe los anchos fraccionarios del layout, que si no
    // dejan el botón «siguiente» activo sobre un carril ya terminado.
    const overflows = scrollWidth - clientWidth > 2;
    this.toggleAttribute('data-rail-static', !overflows);
    if (this.prev) this.prev.disabled = !overflows || scrollLeft <= 2;
    if (this.next) this.next.disabled = !overflows || scrollLeft >= scrollWidth - clientWidth - 2;
  }
}

if (!customElements.get('gv-rail')) {
  customElements.define('gv-rail', GvRail);
}
